import { addDays, dayOf, formatDayMonth, formatShortDay, formatTimeOfDay, instantOf, periodMonthLower, periodOf, shortDayMonth, timeOf, } from "./period.js";
/** [P1] The review's page. */
export const REVIEW_PAGE_PREFIX = "/r/";
export function reviewPagePath(token) {
    return `${REVIEW_PAGE_PREFIX}${token}`;
}
const REVIEW_WINDOW_DAYS = 30; // write after it went out; correct a review; correct a reply
export const REVIEW_FRESH_DAYS = 7; // born only for a service of the last 7 days (today and the 6 before)
export const REVIEW_PER_FILE_DAYS = 30; // [P5] one invitation per file and business every 30 days
const REVIEW_DELAY_HOURS = 2;
/** `DEFAULT_ATTENDED_HOURS`' hours: an invitation never goes out at night. */
const REVIEW_DAY = { opens: "08:00", closes: "20:00" };
const REVIEW_AVERAGE_FROM = 3;
export const PROFILE_REVIEWS_SHOWN = 5;
export const REVIEWS_PAGE = 10;
export const MAX_REVIEW_COMMENT = 1000;
export const MAX_REVIEW_REPLY = 1000;
export const MAX_REVIEW_AUTHOR_NAME = 80;
const MAX_SERVICE_LABEL = 60;
export const MAX_REPORT_NOTE = 500;
export const MAX_REMOVED_REASON = 300;
export const REVIEW_STAR_NAMES = {
    5: "Excelente",
    4: "Buena",
    3: "Regular",
    2: "Mala",
    1: "Muy mala",
};
export const PUBLIC_REVIEW_KEYS = ["id", "stars", "comment", "seal", "writtenAt", "reply"];
export const NO_PROFILE_REVIEWS = { count: 0, sumStars: 0, latest: [], next: null };
/** `review_report.reason`. */
export const REPORT_REASONS = ["offensive", "personal_data", "not_a_client", "other"];
export const REPORT_REASON_LABELS = {
    offensive: "Insulta o usa lenguaje ofensivo",
    personal_data: "Publica datos personales",
    not_a_client: "No la escribió mi cliente",
    other: "Otra razón",
};
/** `review_report.source` (Enmienda 4). */
export const REPORT_SOURCES = ["business", "visitor"];
/** What a visitor may say (Enmienda 4): never «No la escribió mi cliente», which only the business can know. */
export const VISITOR_REPORT_REASONS = ["offensive", "personal_data", "other"];
/** The Turnstile action of the profile's report; the server compares it, so an /ops login token is not spent here. */
export const REVIEW_REPORT_TURNSTILE_ACTION = "review_report";
/** What is missing in a report, said on the press; null when it may go. */
export function reportDraftProblem(draft) {
    if (draft.reason === null)
        return REVIEW_COPY.reasonRequired;
    if (draft.reason === "other" && draft.note.trim() === "")
        return REVIEW_COPY.otherNeedsNote;
    return null;
}
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;
/**
 * When the invitation goes out: two hours after the service, but never at
 * night -- from 20:00 to 08:00 of the business's own zone it waits for 08:00.
 */
export function reviewSendAt(eventAt, timezone) {
    const later = new Date(eventAt.getTime() + REVIEW_DELAY_HOURS * HOUR_MS);
    const at = later.toISOString();
    const day = dayOf(at, timezone);
    const time = timeOf(at, timezone);
    if (time >= REVIEW_DAY.closes)
        return new Date(instantOf(addDays(day, 1), REVIEW_DAY.opens, timezone));
    if (time < REVIEW_DAY.opens)
        return new Date(instantOf(day, REVIEW_DAY.opens, timezone));
    return later;
}
/** The thirtieth day is included. */
export function withinReviewWindow(since, now) {
    return now.getTime() <= Date.parse(since) + REVIEW_WINDOW_DAYS * DAY_MS;
}
export function reviewWindowEnd(since) {
    return new Date(Date.parse(since) + REVIEW_WINDOW_DAYS * DAY_MS).toISOString();
}
/** [P7] The first line's name, else the description's first line, else «Servicio»; cut to the column in code points. */
export function reviewServiceLabelOf(firstLineName, description) {
    const fromDescription = (description ?? "").trim().split("\n")[0]?.trim() ?? "";
    const text = [(firstLineName ?? "").trim(), fromDescription].find((candidate) => candidate !== "") ?? "Servicio";
    return Array.from(text).slice(0, MAX_SERVICE_LABEL).join("").trim();
}
/** Who signs a review whose file was deleted, and the seal of a name with nothing left to print. */
export const REVIEW_NO_NAME = "Cliente";
/**
 * «Juan Pérez» → «Juan P.»: the surname is never printed, nor a file's phone
 * or counter («Felipe (6523-1245)», «Felipe 2» → «Felipe»): every word with a
 * digit or a parenthesis goes first. Nothing left, «Cliente».
 */
export function sealNameOf(name) {
    const words = name.split(/\s+/).filter((word) => word !== "" && !/[\d()]/.test(word));
    const first = words[0] ?? REVIEW_NO_NAME;
    const last = words[words.length - 1] ?? "";
    if (words.length < 2)
        return first;
    const initial = Array.from(last)[0] ?? "";
    return `${first} ${initial.toLocaleUpperCase("es")}.`;
}
const VERIFIED_CLIENT = "Cliente verificado";
/** «Juan P. · Cliente verificado · Limpieza de aire · marzo 2026» */
export function sealLine(seal) {
    return `${seal.name} · ${VERIFIED_CLIENT} · ${seal.serviceLabel} · ${periodMonthLower(periodOf(seal.servedOn))} ${seal.servedOn.slice(0, 4)}`;
}
/** The average shows from three reviews, rounded half up in integers. */
export function reviewSummaryOf(count, sumStars) {
    if (count < REVIEW_AVERAGE_FROM)
        return { count, average: null, ratingValue: null };
    const tenths = Math.floor((20 * sumStars + count) / (2 * count));
    const whole = Math.floor(tenths / 10);
    const tenth = tenths % 10;
    return { count, average: `${whole},${tenth}`, ratingValue: `${whole}.${tenth}` };
}
export function reviewSummaryLine(summary) {
    return summary.average === null ? null : `★ ${summary.average} · ${summary.count} reseñas`;
}
/** «Perfil público»'s «Reseñas» row and its screen: the average from three, the count before, the explanation with none. */
export function profileReviewsLine(reviews) {
    if (reviews.count === 0)
        return REVIEW_COPY.noReviewsYet;
    return reviewSummaryLine(reviewSummaryOf(reviews.count, reviews.sumStars)) ?? REVIEW_COPY.countLine(reviews.count);
}
export function reviewSignLine(summary) {
    return summary.average === null ? null : `★ ${summary.average} (${summary.count})`;
}
/** Never offers anything and never asks for stars (spec §5.3). */
export function reviewInviteMessage(input) {
    return `Hola. ¿Cómo te fue con ${input.serviceLabel}? Cuéntanos tu opinión aquí: ${input.url}`;
}
/**
 * What the document's card says of an invitation that has not been answered.
 * Nothing while VozQI cannot reach her (no account, no notice to promise); the WhatsApp button beside it is how she gets it.
 */
export function orderReviewLine(card, now, timezone) {
    if ((card.state === "scheduled" || card.state === "sent") && !card.reachable)
        return null;
    switch (card.state) {
        case "scheduled": {
            const day = dayOf(card.sendAt, timezone);
            const today = dayOf(now.toISOString(), timezone);
            const when = day === today ? "hoy" : day === addDays(today, 1) ? "mañana" : `el ${formatDayMonth(day)}`;
            return `Le pediremos su opinión ${when} a las ${formatTimeOfDay(timeOf(card.sendAt, timezone))}`;
        }
        case "sent":
            return `Le pedimos su opinión el ${shortDayMonth(dayOf(card.sentAt, timezone))}.`;
        case "expired":
            return "No dejó reseña.";
        case "withdrawn":
            return "El cliente borró su reseña.";
        case "written":
            return null;
    }
}
export const REVIEWS_EXPLANATION = "Sólo se escribe desde la invitación que VozQI manda cuando este negocio entrega o cobra un servicio en la app: un enlace único, para una sola reseña. El negocio no puede borrarla ni ocultarla.";
/** The section of the terms the line under «Publicar» opens. */
export const REVIEW_TERMS_PATH = "/terminos#resenas";
export const REVIEW_COPY = {
    pageTitle: "¿Cómo te fue con tu servicio?",
    commentLabel: "Cuéntanos más (opcional)",
    nameLabel: "Tu nombre",
    savedAs: (name) => `Saldrá como ${sealNameOf(name)}`,
    /** After «Saldrá como …»: shows the name's field, filled with the name it signs with. */
    changeName: "Cambiar",
    publish: "Publicar",
    /** A correction's button: her review's, and the business's reply's. */
    save: "Guardar",
    starsLabel: (n) => (n === 1 ? "1 estrella" : `${n} estrellas`),
    thanks: (business) => `Gracias. Tu reseña ya está en el perfil de ${business}.`,
    seeProfile: "Ver el perfil",
    correct: "Corregir",
    remove: "Borrar",
    removeConfirm: "¿Borrar tu reseña? No podrás escribirla de nuevo.",
    taken: "Esta reseña ya se escribió.",
    expired: "Esta invitación ya venció.",
    gone: "Este enlace ya no está disponible.",
    /** «¿Cómo te fue?» on her service, written when the service no longer earns it: undelivered, cancelled, the profile hidden. */
    notReviewable: "Este servicio no se puede reseñar.",
    member: "No puedes reseñar un negocio en el que trabajas.",
    windowClosed: "Ya pasaron 30 días: esta reseña ya no se corrige.",
    removeWindowClosed: "Ya pasaron 30 días: esta reseña ya no se borra desde el enlace.",
    claimWindowClosed: "Ya pasaron 30 días: esta reseña ya no se puede guardar en una cuenta.",
    replyWindowClosed: "Ya pasaron 30 días: esta respuesta ya no se corrige.",
    starsRequired: "Elige de 1 a 5 estrellas.",
    nameRequired: "Escribe tu nombre.",
    commentTooLong: `Tu comentario puede tener hasta ${MAX_REVIEW_COMMENT} caracteres.`,
    cardTitle: "Reseña",
    clientCardTitle: "¿Cómo te fue?",
    askWhatsapp: "Pedir reseña por WhatsApp",
    reply: "Responder",
    report: "Denunciar",
    reportTitle: "Denunciar reseña",
    reasonRequired: "Elige la razón de la denuncia.",
    reportNoteLabel: "Nota (opcional)",
    otherNoteLabel: "Cuéntanos la razón",
    otherNeedsNote: "Cuéntanos la razón.",
    reported: "Denunciada · en revisión",
    removedLine: (reason) => `Retirada por VozQI: ${reason}`,
    replyHeading: (business, day) => `Respuesta de ${business} · ${formatShortDay(day)}`,
    replyRequired: "Escribe tu respuesta.",
    alreadyReplied: "Ya respondiste esta reseña.",
    alreadyReported: "Ya la denunciaste: VozQI la está revisando.",
    keptBefore: "VozQI ya revisó esta reseña y sigue publicada.",
    removedRefusal: "VozQI retiró esta reseña.",
    /** A review id that is not the business's, or not written: the business's 404. */
    reviewGone: "Esta reseña ya no existe.",
    reportEmailLabel: "Tu correo (opcional, para responderte)",
    reportEmailInvalid: "Revisa el correo.",
    reportSend: "Enviar denuncia",
    reportSent: "Gracias. VozQI la va a revisar; mientras tanto sigue publicada.",
    reportCaptcha: "Confirma que no eres un robot y vuelve a enviar.",
    reportFailed: "No pudimos enviar la denuncia. Prueba otra vez.",
    memberReports: "Denúnciala desde «Perfil público» → «Reseñas».",
    profileBlockTitle: "Reseñas",
    seeMore: "Ver más reseñas",
    whatItMeans: "¿Qué significa?",
    /** The WhatsApp card of `/r/` (spec §9.3). */
    shareTitle: (business) => `¿Cómo te fue con ${business}?`,
    shareDescription: "Cuéntanos cómo te fue con tu servicio.",
    /** «Perfil público»'s row with fewer than three reviews. */
    countLine: (n) => (n === 1 ? "1 reseña" : `${n} reseñas`),
    noReviewsYet: "Todavía no tienes reseñas. Cada cliente recibe la invitación dos horas después de que le entregas o le cobras.",
    /** The card under «Gracias» of a review published signed out (Enmienda 1). */
    accountOffer: (business) => `Guarda tus servicios de ${business} en VozQI: te avisa cuando te toque y ves tus cotizaciones`,
    createAccount: "Crear mi cuenta",
    claimed: (business) => `Listo. Tu reseña quedó en tu cuenta y le pedimos a ${business} que te agregue como cliente.`,
    claimedOnly: "Listo. Tu reseña quedó en tu cuenta.",
    goToApp: "Ir a VozQI",
    termsLine: "Al publicar aceptas los términos de VozQI para reseñas.",
};
/**
 * The stars, and the name only when «Cambiar» showed its field (Enmienda 3):
 * without it the server signs with the file's, or keeps the review's. What is
 * missing is a sentence -- never a grey button.
 */
export function reviewInputOf(draft) {
    if (draft.stars === null)
        return { problem: REVIEW_COPY.starsRequired };
    const authorName = draft.authorName?.trim() ?? null;
    if (authorName === "")
        return { problem: REVIEW_COPY.nameRequired };
    return { stars: draft.stars, comment: draft.comment.trim(), authorName };
}
